import { BillCategoryType, BillItemFragment, useCreateBillMutation } from 'src/api/api.generated'
import { BillData } from './bill-creation-modal'
import { useCallback, useState } from 'react'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import useSuccessSnackBar from 'src/app/hooks/success-snack-bar/use-success-snack-bar'
import { useTranslation } from 'react-i18next'

type useBillCreationProps = {
  propertyId: string
  closeCreationDialog: () => void
  onChange?: (value: BillItemFragment | null) => void
  reservationIds?: string[]
  reservedResourceIds?: string[]
  defaultNewBillCategory?: BillCategoryType
}

export function useBillCreation(props: useBillCreationProps) {
  const { t } = useTranslation()
  const { id: tenantId } = useTenant()
  const showErrorSnackBar = useErrorSnackBar()
  const showSuccessSnackBar = useSuccessSnackBar()

  const [createBillMutation] = useCreateBillMutation()
  const [createNewBillLoading, setCreateNewBillLoading] = useState(false)

  const createNewBill = useCallback(
    async (bill: BillData) => {
      try {
        setCreateNewBillLoading(true)
        const result = await createBillMutation({
          variables: {
            value: {
              tenantId: tenantId,
              propertyId: props.propertyId,
              ownerId: bill.ownerId,
              category: props.defaultNewBillCategory ?? BillCategoryType.EXTRA,
              description: bill.description,
              reservationId: props.reservationIds ? props.reservationIds : undefined,
              reservedResourceId: bill.reservedResourceId ? [bill.reservedResourceId] : undefined,
            },
          },
          refetchQueries: ['ReservationDetail'],
        })
        setCreateNewBillLoading(false)

        const newBill = result.data?.createBill
        if (newBill === undefined) {
          showErrorSnackBar(result.errors)
        } else {
          props.onChange?.(newBill)
          showSuccessSnackBar(t('pms.booking.passerby-bill.creation-dialog.create_success'))
        }
        props.closeCreationDialog()
      } catch (e) {
        setCreateNewBillLoading(false)
        showErrorSnackBar(e)
      }
    },
    [createBillMutation, tenantId, props.propertyId, createNewBillLoading, showErrorSnackBar, props.closeCreationDialog],
  )

  return { createNewBill, createNewBillLoading }
}
